import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { getLanguageDisplayInfo } from '../constants/languages';
import { useTheme } from '../components/ThemeContext';
import { LanguageGate } from '../components/TribalBorder';
import type { LanguageCode } from '../types/types';
import './LanguageFirstSelection.css';

import { EmojiText, Picto } from '../components/common/Picto';
interface LanguageFirstSelectionProps {
  onLanguageSelect: (language: LanguageCode) => void;
  isVisible: boolean;
}

// Registre des portes (maquette Tour 17 · 17a) : chaque langue se tient
// dans une niche ocre avec la porte de son bandeau (voir TribalBorder).
const REGISTRY: Record<string, { cc: string; country: string; local?: string }> = {
  wf: { cc: 'SN', country: 'Sénégal' },
  ba: { cc: 'ML', country: 'Mali', local: 'Bamanankan' },
  la: { cc: 'CD', country: 'Congo', local: 'Lingála' },
  ff: { cc: 'GN', country: 'Guinée', local: 'Pulaar' },
  co: { cc: 'EG', country: 'Égypte antique' },
  sw: { cc: 'TZ', country: 'Tanzanie', local: 'Kiswahili' },
  ka: { cc: 'EG', country: 'Égypte antique', local: 'Medou netjer' },
  yo: { cc: 'NG', country: 'Nigeria', local: 'Yorùbá' },
};

const GateNiche: React.FC<{ code: LanguageCode; className: string }> = ({ code, className }) => (
  <span className={className} aria-hidden="true">
    <LanguageGate lang={code} ibis={false} />
  </span>
);

const LanguageFirstSelection: React.FC<LanguageFirstSelectionProps> = ({
  onLanguageSelect,
  isVisible
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const languages = getLanguageDisplayInfo();
  const languageArray = Object.entries(languages) as [LanguageCode, typeof languages[LanguageCode]][];

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleSelect = useCallback((language: LanguageCode) => {
    if (isAnimating) return;
    setSelectedLanguage(language);
    setIsAnimating(true);
    if ('vibrate' in navigator) {
      try { navigator.vibrate([30, 15, 30]); } catch (_) {}
    }
    setTimeout(() => { onLanguageSelect(language); }, 700);
  }, [isAnimating, onLanguageSelect]);

  useEffect(() => {
    if (isMobile) return;
    const handleKey = (e: KeyboardEvent) => {
      if (!isVisible || isAnimating) return;
      const num = parseInt(e.key);
      if (num >= 1 && num <= languageArray.length) handleSelect(languageArray[num - 1][0]);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isVisible, isAnimating, isMobile, languageArray, handleSelect]);

  if (!isVisible) return null;

  const themeToggleButton = (
    <button
      className="lfs-theme-btn"
      onClick={toggleTheme}
      aria-label={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
      title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
      type="button"
    >
      <EmojiText text={theme === 'dark' ? '☀️' : '🌙'} />
    </button>
  );

  /* ─────────────────────────────
     MOBILE
  ───────────────────────────── */
  if (isMobile) {
    return (
      <div className="lfs-overlay">
        {/* Bandeau plein — même identité que le header de l'app */}
        <div className="lfs-topbar">
          <div className="lfs-topbar-left">
            <span className="lfs-topbar-logo" aria-hidden="true"><img src="/assets/logo-mark.svg" alt="" /></span>
            <span className="lfs-topbar-title">Jogolinga</span>
          </div>
          {themeToggleButton}
        </div>
        <div className="lfs-topbar-zigzag" aria-hidden="true" />

        {/* Décoration fond */}
        <div className="lfs-deco" aria-hidden="true">
          <div className="lfs-deco-circle lfs-deco-circle--a" />
          <div className="lfs-deco-circle lfs-deco-circle--b" />
          <span className="lfs-deco-glyph lfs-deco-glyph--eye">𓂀</span>
          <span className="lfs-deco-glyph lfs-deco-glyph--ankh">𓋹</span>
        </div>

        {/* En-tête */}
        <motion.header
          className="lfs-header"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <h1 className="lfs-title">Quelle langue<br />voulez-vous apprendre ?</h1>
          <p className="lfs-subtitle">{languageArray.length} langues africaines vous attendent</p>
          <div className="lfs-hiero-divider" aria-hidden="true">𓏤 𓂀 𓋹 𓂀 𓏤</div>
        </motion.header>

        {/* Liste des langues */}
        <motion.div
          className="lfs-list"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.35, ease: 'easeOut' }}
        >
          {languageArray.map(([code, { name, description }], i) => {
            const isSelected = selectedLanguage === code;
            const isFading = isAnimating && !isSelected;
            const reg = REGISTRY[code];

            return (
              <motion.button
                key={code}
                className="lfs-lang-btn"
                data-selected={isSelected}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: isFading ? 0.22 : 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.06, duration: 0.28 }}
                whileTap={!isAnimating ? { scale: 0.975 } : {}}
                onClick={() => handleSelect(code)}
                disabled={isAnimating}
                aria-label={`${name} — ${description}`}
              >
                <GateNiche code={code} className="lfs-gate" />

                {/* Texte : nom, nom local, code et pays */}
                <span className="lfs-lang-text">
                  <span className="lfs-lang-title">
                    <span className="lfs-lang-name">{name}</span>
                    {reg?.local && <span className="lfs-lang-local">{reg.local}</span>}
                  </span>
                  <span className="lfs-lang-meta">
                    {reg ? `${reg.cc} · ${reg.country}` : description}
                  </span>
                </span>

                {/* Icône droite */}
                <span className="lfs-lang-right" aria-hidden="true">
                  {isSelected ? (
                    <motion.span
                      className="lfs-lang-check"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    >
                      {isAnimating
                        ? <motion.span
                            animate={{ rotate: 360 }}
                            transition={{ duration: 0.6, repeat: Infinity, ease: 'linear' }}
                            style={{ display: 'block' }}
                          >⟳</motion.span>
                        : <span className="lfs-check-mark" />}
                    </motion.span>
                  ) : (
                    <span className="lfs-lang-arrow" />
                  )}
                </span>
              </motion.button>
            );
          })}
        </motion.div>
      </div>
    );
  }

  /* ─────────────────────────────
     DESKTOP
  ───────────────────────────── */
  return (
      <motion.div
        className="lfs-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Bandeau plein — même identité que le header de l'app */}
        <div className="lfs-topbar">
          <div className="lfs-topbar-left">
            <span className="lfs-topbar-logo" aria-hidden="true"><img src="/assets/logo-mark.svg" alt="" /></span>
            <span className="lfs-topbar-title">Jogolinga</span>
          </div>
          {themeToggleButton}
        </div>
        <div className="lfs-topbar-zigzag" aria-hidden="true" />

        <div className="lfs-deco" aria-hidden="true">
          <div className="lfs-deco-circle lfs-deco-circle--a" />
          <div className="lfs-deco-circle lfs-deco-circle--b" />
          <span className="lfs-deco-glyph lfs-deco-glyph--eye">𓂀</span>
          <span className="lfs-deco-glyph lfs-deco-glyph--ankh">𓋹</span>
        </div>

        <motion.div
          className="lfs-body"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.35 }}
        >
          <p className="lfs-body-sub">Cliquez sur une langue pour commencer</p>
          <div className="lfs-hiero-divider" aria-hidden="true">𓏤 𓂀 𓋹 𓂀 𓏤</div>
          <span className="lfs-keyboard-hint">
            <Picto name="lampe" /> Touches 1–{languageArray.length} pour sélectionner
          </span>

          <div className="lfs-grid">
            {languageArray.map(([code, { name, description }], i) => {
              const isSelected = selectedLanguage === code;
              const isFading = isAnimating && !isSelected;
              const reg = REGISTRY[code];

              return (
                <motion.button
                  key={code}
                  className="lfs-tile"
                  data-selected={isSelected}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{
                    scale: isFading ? 0.88 : isSelected ? 1.04 : 1,
                    opacity: isFading ? 0.2 : 1,
                  }}
                  transition={{ delay: 0.3 + i * 0.07, type: 'spring', stiffness: 220 }}
                  whileHover={!isAnimating && !isSelected ? { y: -3, transition: { duration: 0.18 } } : {}}
                  onClick={() => handleSelect(code)}
                  disabled={isAnimating}
                >
                  <span className="lfs-tile-num">{i + 1}</span>

                  {isSelected && (
                    <motion.div
                      className="lfs-tile-shine"
                      initial={{ x: '-100%' }}
                      animate={{ x: '220%' }}
                      transition={{ duration: 0.6 }}
                      aria-hidden="true"
                    />
                  )}

                  <GateNiche code={code} className="lfs-gate lfs-gate--tile" />

                  <span className="lfs-tile-name">{name}</span>
                  {reg?.local && <span className="lfs-lang-local">{reg.local}</span>}
                  <span className="lfs-lang-meta">
                    {reg ? `${reg.cc} · ${reg.country}` : description}
                  </span>

                  {isSelected && (
                    <motion.span
                      className="lfs-tile-check"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                    >
                      {isAnimating
                        ? <motion.span
                            animate={{ rotate: 360 }}
                            transition={{ duration: 0.6, repeat: Infinity, ease: 'linear' }}
                            style={{ display: 'block', fontSize: 15 }}
                          >⟳</motion.span>
                        : <span className="lfs-check-mark" />}
                    </motion.span>
                  )}
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </motion.div>
  );
};

export default LanguageFirstSelection;
